import React, { FC } from 'react';
import { useQuery } from '@tanstack/react-query';

import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiServiceLinksRetrieve,
  getApiServiceLinksRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-service-links-retrieve';
import { ApiServiceLinksRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiServiceLinksRetrieve200';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import type { PaginatedBrandLocalityServiceCitiesItemList } from '@rover/rsdk/src/apiClient/latest/model/paginatedBrandLocalityServiceCitiesItemList';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';
import getParam from '@rover/shared/js/utils/getParams';
import {
  isServiceLanding,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
} from '@rover/types/src/ServiceLanding';
import { Http500Error } from '@rover/utilities/HttpError';
import { getCountryUrlPrefix, US_COUNTRY_URL_PREFIX } from '@rover/utilities/roverGeo.constants';

import ServiceLandingPage from '../../ServiceLandingPage';
import { ServiceLandingContainerProps, ServiceLandingEngineContainer } from '../../types';
import { getServiceLandingSearchQueryOptions } from '../../utilities/getServiceLandingSearchQueryOptions';
import useServiceLandingPageData from '../../utils/useServiceLandingPageData';

const EMPTY_SERVICE_LINKS: ApiServiceLinksRetrieve200 = { data: [] };

const getCitiesAndNeighborhoods = (
  citiesAndNeighborhoods: PaginatedBrandLocalityServiceCitiesItemList['results']
): Array<{ title: string; href: string }> =>
  (citiesAndNeighborhoods ?? [])
    .filter((c) => c.city && c.seoSearchPath)
    .map((c) => ({
      title: c.city!,
      href: c.seoSearchPath!,
    }));

const LegacyServiceLandingContainer: FC<ServiceLandingContainerProps> = ({
  serviceLandingData,
  descriptor,
}) => {
  const { countryUrlPrefix } = descriptor;
  const {
    citiesAndNeighborhoods,
    countryConfig,
    countryFooterLinks,
    countryRegionLinks,
    locale,
    settings,
    serviceTypes,
    isLoading,
    isError,
  } = useServiceLandingPageData(descriptor, serviceLandingData);

  const { serviceLinksForCurrentRoute, serviceLinksWithDisplayName } = useServiceLinksShim();

  const { data: searchResults } = useQuery(
    getServiceLandingSearchQueryOptions(serviceLandingData, EngineEnum.legacy)
  );

  if (isError) throw new Http500Error();

  if (isLoading) {
    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  if (
    !countryConfig ||
    !citiesAndNeighborhoods ||
    !settings ||
    !serviceTypes ||
    !countryRegionLinks
  ) {
    return null;
  }

  // Derive the landing type from the LocalizedServiceKeyword-backed serviceLandingData
  // (its url slug shares the ServiceLandingEnum string values) rather than from the
  // serviceLinks routing array, so the page renders even when service links are missing.
  // TODO: drop the ServiceLandingEnum bridge and consume the LSK data directly.
  const serviceLandingType = isServiceLanding(serviceLandingData.serviceLandingUrlSlug)
    ? serviceLandingData.serviceLandingUrlSlug
    : null;

  // The legacy pages are keyed by ServiceLandingEnum, so a slug outside it has no page to
  // render. ServiceLandingPage throws the same way; failing here keeps the type honest.
  if (!serviceLandingType) throw new Http500Error();

  const brandServiceLinks = (serviceLinksWithDisplayName ?? []).filter(
    ({ href }) => (getCountryUrlPrefix(href) ?? US_COUNTRY_URL_PREFIX) === countryUrlPrefix
  );

  return (
    <ServiceLandingPage
      serviceLinksWithDisplayName={brandServiceLinks}
      locale={locale}
      countryConfig={countryConfig}
      serviceLandingData={serviceLandingData}
      serviceTypes={serviceTypes}
      searchResults={searchResults}
      citiesAndNeighborhoodsLinks={citiesAndNeighborhoods}
      countryFooterLinks={countryFooterLinks}
      countryRegionLinks={countryRegionLinks}
      serviceLinks={serviceLinksForCurrentRoute}
      serviceLandingType={serviceLandingType}
      tags={getParam('tags')}
      citiesAndNeighborhoods={getCitiesAndNeighborhoods(citiesAndNeighborhoods)}
      serviceTypeSlug={SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType]}
      frontendConfigSettings={settings}
    />
  );
};

const reactQueryPrefetch: ServiceLandingEngineContainer['reactQueryPrefetch'] = (queryClient) =>
  queryClient.prefetchQuery({
    queryKey: getApiServiceLinksRetrieveQueryKey(),
    queryFn: () => apiServiceLinksRetrieve().catch(() => EMPTY_SERVICE_LINKS),
  });

export default Object.assign(LegacyServiceLandingContainer, { reactQueryPrefetch });
